export const LOADER_TIMING = Object.freeze({
	SHOW_DELAY: 150,
	MIN_SHOW_TIME: 400,
	WARNING_DELAY: 20_000,
	VISUAL_RELEASE_DELAY: 60_000,
})

export const LONG_RUNNING_MESSAGE = '處理時間較長，請勿重複操作'

export class LoaderController {
	constructor({
		setTimer = (...args) => globalThis.setTimeout(...args),
		clearTimer = (...args) => globalThis.clearTimeout(...args),
		now = Date.now,
		onChange = () => {},
	} = {}) {
		this.setTimer = setTimer
		this.clearTimer = clearTimer
		this.now = now
		this.onChange = onChange
		this.active = new Map()
		this.nextId = 1
		this.sequence = 0
	}

	show(message = '載入中，請稍候...', options = {}) {
		const token = Object.freeze({ id: this.nextId++ })
		const entry = {
			token,
			owner: options.owner ?? null,
			message,
			immediate: options.immediate === true,
			startedAt: this.now(),
			warned: false,
			visualExpired: false,
			sequence: ++this.sequence,
			warningTimer: null,
			releaseTimer: null,
		}

		this.active.set(token.id, entry)
		entry.warningTimer = this.setTimer(
			() => this.markWarned(token),
			LOADER_TIMING.WARNING_DELAY,
		)
		entry.releaseTimer = this.setTimer(
			() => this.releaseVisual(token),
			LOADER_TIMING.VISUAL_RELEASE_DELAY,
		)
		this.emit('show')

		return token
	}

	hide(token) {
		const entry = this.getEntry(token)
		if (!entry) return false

		this.removeEntry(entry)
		this.emit('hide')
		return true
	}

	clearOwner(owner) {
		const matches = [...this.active.values()].filter(
			entry => entry.owner === owner,
		)

		for (const entry of matches) {
			this.removeEntry(entry)
		}

		if (matches.length > 0) {
			this.emit('clear-owner')
		}

		return matches.length
	}

	clearAll() {
		const entries = [...this.active.values()]

		for (const entry of entries) {
			this.removeEntry(entry)
		}

		if (entries.length > 0) {
			this.emit('clear-all')
		}

		return entries.length
	}

	getSnapshot() {
		const visualEntries = [...this.active.values()].filter(
			entry => !entry.visualExpired,
		)
		const newest = visualEntries.reduce(
			(selected, entry) =>
				!selected || entry.sequence > selected.sequence ? entry : selected,
			null,
		)
		const hasWarning = visualEntries.some(entry => entry.warned)

		return {
			activeCount: this.active.size,
			visualCount: visualEntries.length,
			shouldShow: visualEntries.length > 0,
			immediate: visualEntries.some(entry => entry.immediate),
			message: newest
				? hasWarning
					? LONG_RUNNING_MESSAGE
					: newest.message
				: '',
		}
	}

	getEntry(token) {
		if (!token || typeof token.id !== 'number') return null

		const entry = this.active.get(token.id)
		return entry?.token === token ? entry : null
	}

	markWarned(token) {
		const entry = this.getEntry(token)
		if (!entry || entry.visualExpired) return

		entry.warningTimer = null
		entry.warned = true
		this.emit('warning')
	}

	releaseVisual(token) {
		const entry = this.getEntry(token)
		if (!entry || entry.visualExpired) return

		entry.releaseTimer = null
		entry.visualExpired = true
		this.clearEntryTimer(entry, 'warningTimer')
		this.emit('visual-release')
	}

	removeEntry(entry) {
		this.clearEntryTimer(entry, 'warningTimer')
		this.clearEntryTimer(entry, 'releaseTimer')
		this.active.delete(entry.token.id)
	}

	clearEntryTimer(entry, timerName) {
		if (entry[timerName] === null) return

		this.clearTimer(entry[timerName])
		entry[timerName] = null
	}

	emit(type) {
		this.onChange(this.getSnapshot(), { type })
	}
}
